import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { TrendingUp, DollarSign, Briefcase, Target, Calendar } from 'lucide-react';
import { CATEGORICAL } from '../constants/palette';

const API_BASE = '/api';

const COLORS = CATEGORICAL;

const formatRM = (value) => {
  if (value >= 1000000) return `RM ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `RM ${(value / 1000).toFixed(0)}k`;
  return `RM ${value.toFixed(0)}`;
};

const formatFullRM = (value) =>
  `RM ${parseFloat(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--rmc-white)', padding: '10px 14px', borderRadius: 8,
      border: '1px solid var(--rmc-line)', boxShadow: '0 6px 20px rgba(59, 91, 219, 0.18)', fontSize: '0.85rem'
    }}>
      <p style={{ fontWeight: 600, marginBottom: 4, color: 'var(--rmc-ink)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {p.name === 'Jobs' ? p.value : formatFullRM(p.value)}
        </p>
      ))}
    </div>
  );
};

function SalesAnalytics({ salespersonId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/salesperson/${salespersonId}/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [salespersonId, token]);

  if (loading) return <div className="loading">Loading analytics...</div>;
  if (!data) return null;

  const { monthly_stats, revenue_trend, event_type_breakdown, venue_type_breakdown } = data;

  const statCards = [
    { label: 'Monthly Revenue', value: formatFullRM(monthly_stats.current_month_revenue), icon: DollarSign, color: CATEGORICAL[0] },
    { label: 'Commission', value: formatFullRM(monthly_stats.current_month_commission), icon: TrendingUp, color: CATEGORICAL[1] },
    { label: 'Jobs Closed', value: monthly_stats.jobs_closed, icon: Briefcase, color: CATEGORICAL[2] },
    { label: 'Avg Deal Size', value: formatFullRM(monthly_stats.avg_deal_size), icon: Target, color: CATEGORICAL[4] },
    { label: 'YTD Revenue', value: formatFullRM(monthly_stats.ytd_total), icon: Calendar, color: CATEGORICAL[3] },
  ];

  return (
    <div className="analytics-section">
      <h3>Sales Analytics (2026 YTD)</h3>

      {/* Stat Cards */}
      <div className="analytics-stats">
        {statCards.map((card, i) => (
          <div className="analytics-stat-card" key={i}>
            <div className="analytics-stat-icon" style={{ background: `${card.color}15`, color: card.color }}>
              <card.icon size={20} />
            </div>
            <div>
              <p className="analytics-stat-label">{card.label}</p>
              <p className="analytics-stat-value">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Trend */}
      {revenue_trend.length > 0 && (
        <div className="analytics-chart-card">
          <h4>Monthly Revenue Trend</h4>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={revenue_trend} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--rmc-ink2)' }} />
              <YAxis tickFormatter={formatRM} tick={{ fontSize: 12, fill: 'var(--rmc-ink2)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone" dataKey="revenue" name="Revenue"
                stroke={CATEGORICAL[0]} strokeWidth={3} dot={{ r: 5, fill: CATEGORICAL[0] }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Event Type + Venue Breakdown */}
      <div className="analytics-charts-row">
        {event_type_breakdown.length > 0 && (
          <div className="analytics-chart-card">
            <h4>Revenue by Event Type</h4>
            <ResponsiveContainer width="100%" height={Math.max(250, event_type_breakdown.length * 38)}>
              <BarChart data={event_type_breakdown} layout="vertical"
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" horizontal={false} />
                <XAxis type="number" tickFormatter={formatRM} tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} />
                <YAxis type="category" dataKey="event_type" tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={22}>
                  {event_type_breakdown.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="analytics-table-mini">
              <div className="analytics-table-row analytics-table-header">
                <span className="analytics-dot" style={{ opacity: 0 }} />
                <span className="analytics-table-label"><strong>Type</strong></span>
                <span className="analytics-table-count"><strong>Jobs</strong></span>
                <span className="analytics-table-avg"><strong>Avg Basket</strong></span>
                <span className="analytics-table-value"><strong>Revenue</strong></span>
              </div>
              {event_type_breakdown.map((e, i) => (
                <div className="analytics-table-row" key={i}>
                  <span className="analytics-dot" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="analytics-table-label">{e.event_type}</span>
                  <span className="analytics-table-count">{e.job_count}</span>
                  <span className="analytics-table-avg">{formatFullRM(e.job_count > 0 ? e.revenue / e.job_count : 0)}</span>
                  <span className="analytics-table-value">{formatFullRM(e.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {venue_type_breakdown.length > 0 && (
          <div className="analytics-chart-card">
            <h4>Revenue by Venue / Brand</h4>
            <ResponsiveContainer width="100%" height={Math.max(250, venue_type_breakdown.length * 38)}>
              <BarChart data={venue_type_breakdown} layout="vertical"
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--rmc-line-soft)" horizontal={false} />
                <XAxis type="number" tickFormatter={formatRM} tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} />
                <YAxis type="category" dataKey="venue_type" tick={{ fontSize: 11, fill: 'var(--rmc-ink2)' }} width={140} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={22}>
                  {venue_type_breakdown.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="analytics-table-mini">
              <div className="analytics-table-row analytics-table-header">
                <span className="analytics-dot" style={{ opacity: 0 }} />
                <span className="analytics-table-label"><strong>Venue</strong></span>
                <span className="analytics-table-count"><strong>Jobs</strong></span>
                <span className="analytics-table-avg"><strong>Avg Basket</strong></span>
                <span className="analytics-table-value"><strong>Revenue</strong></span>
              </div>
              {venue_type_breakdown.map((v, i) => (
                <div className="analytics-table-row" key={i}>
                  <span className="analytics-dot" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="analytics-table-label">{v.venue_type}</span>
                  <span className="analytics-table-count">{v.job_count}</span>
                  <span className="analytics-table-avg">{formatFullRM(v.job_count > 0 ? v.revenue / v.job_count : 0)}</span>
                  <span className="analytics-table-value">{formatFullRM(v.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesAnalytics;
